import { queryKeys } from '@query/keys'
import type { QueryKey } from '@tanstack/query-core'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Navigate, useNavigate } from '@lib/react-router-dom'

import { useTradeRoutesConditions } from '@pages/Trade/Trade.routes'
import { useTradeTickmillTraderClick } from '@pages/Traders-Room/Trading-Accounts/components/TradeButtonLinkWrapper'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Body2 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { RichIcon } from '@ui/components/RichIcon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { MT4Icon } from '@ui/icons/platforms/MT4.icon'
import { MT5Icon } from '@ui/icons/platforms/MT5.icon'
import { TMTIcon } from '@ui/icons/platforms/TMT.icon'
import { TradingViewIcon } from '@ui/icons/platforms/TradingView.icon'

import { getClientPrimaryEmail } from '@utils/AccountAccess/accountEmailStatuses'
import { Operator, PageData, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path-common'

import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { AccountGroupType } from '@model/AccountGroupType'
import {
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import {
  DemoTradingAccount,
  TradingAccount,
  TradingAccountStatusType,
  isPlatformTypeMT4,
  isPlatformTypeMT5,
  isPlatformTypeTickmillTrader,
  isPlatformTypeTradingView,
} from '@model/TradingAccount'

import { useFundingNav } from '@hooks/useFundingNav'
import { useScrollToTop } from '@hooks/useScrollToTop'
import { useTradingViewTradeUrl } from '@hooks/useTradingViewTradeUrl'

import { Box } from '@shared/components/Box'
import { CopyLink } from '@shared/components/CopyLink'
import { Loading } from '@shared/components/Loading/Loading'

const EMPTY_ACCOUNTS_ERROR = 'EmptyAccountsError'

type TradingAccountType = TradingAccount | DemoTradingAccount

interface TradingAccountsQueryProps<T extends TradingAccountType> {
  queryKey: QueryKey
  fetchFn: () => Promise<PageData<T>>
}

const useTradingAccountsQuery = <T extends TradingAccountType>(
  props: TradingAccountsQueryProps<T>
) => {
  const { queryKey, fetchFn } = props

  const location = useLocation()
  const { account } = useAccountReadContext()

  const result = useQuery({
    queryKey,
    queryFn: async () => {
      const accounts = await fetchFn()

      if (!accounts?.items?.length) {
        throw new Error(EMPTY_ACCOUNTS_ERROR)
      }

      return accounts
    },

    enabled: location.state?.canAccessSuccessPage || false,

    retry: (failureCount, error) =>
      isEmptyAccountsError(error) && failureCount < 3 && isAccountActivatedStatus(account),
    retryDelay: 5000,

    throwOnError: (error) => !isEmptyAccountsError(error),
    refetchOnMount: 'always',
  })

  const [tradingAccount] = result?.data?.items || []

  return { ...result, tradingAccount }
}

const useTradingLiveAccounts = () => {
  const apiClient = useApiClient(ClientApiClient)
  const search = {
    AccountGroupType: { value: AccountGroupType.Live, operator: Operator.EQUAL },
    Status: { value: TradingAccountStatusType.Approved, operator: Operator.EQUAL },
  }

  return useTradingAccountsQuery({
    queryKey: queryKeys.tradingAccount.live(search),
    fetchFn: () => apiClient.getTradingAccounts(search),
  })
}

export const CreateTradingAccountCampaignLiveSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()
  const location = useLocation()
  const { canAccessTrade } = useTradeRoutesConditions()
  const tradingViewUrl = useTradingViewTradeUrl()

  const { tradingAccount, isLoading } = useTradingLiveAccounts()

  const { handlePlatformInNewTabOpen } = useTradeTickmillTraderClick({ tradingAccount })

  const handleSubmit = async () => {
    if (!tradingAccount) return

    if (isPlatformTypeTickmillTrader(tradingAccount)) {
      if (canAccessTrade) {
        navigate('/dashboard/trade/markets', {
          state: {
            selectedTradingAccount: { id: tradingAccount.id },
            showReadyToStartTradingModal: true,
          },
        })
      } else {
        await handlePlatformInNewTabOpen()
        navigate('/dashboard/traders-room/trading-accounts/live')
      }
      return
    }

    if (isPlatformTypeMT4(tradingAccount) || isPlatformTypeMT5(tradingAccount)) {
      navigateToFunding(`${CommonPaths.TradersRoomTradingAccounts}/${tradingAccount.id}/deposit`, {
        state: { selectedTradingAccount: { id: tradingAccount.id } },
      })
      return
    }

    if (isPlatformTypeTradingView(tradingAccount)) {
      window.open(tradingViewUrl, '_blank')
      navigate('/dashboard/traders-room/trading-accounts/live')
    }
  }

  if (!location.state?.canAccessSuccessPage) {
    return <Navigate to='/dashboard/traders-room/trading-accounts/live' replace />
  }

  return (
    <CreateTradingAccountCampaignSuccessPageTemplate
      tradingAccount={tradingAccount}
      isLoading={isLoading}
      onSubmit={handleSubmit}
    />
  )
}

const useTradingDemoAccounts = () => {
  const apiClient = useApiClient(ClientApiClient)
  const search = { Status: { value: TradingAccountStatusType.Approved, operator: Operator.EQUAL } }

  return useTradingAccountsQuery({
    queryKey: queryKeys.tradingAccount.demo(search),
    fetchFn: () => apiClient.getDemoTradingAccounts(search),
  })
}

export const CreateTradingAccountCampaignDemoSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { canAccessTrade } = useTradeRoutesConditions()
  const tradingViewUrl = useTradingViewTradeUrl()

  const { tradingAccount, isLoading } = useTradingDemoAccounts()

  const { handlePlatformInNewTabOpen } = useTradeTickmillTraderClick({ tradingAccount })

  const handleSubmit = async () => {
    if (!tradingAccount) return

    if (isPlatformTypeTickmillTrader(tradingAccount)) {
      if (canAccessTrade) {
        navigate('/dashboard/trade/markets', {
          state: { selectedTradingAccount: { id: tradingAccount.id } },
        })
      } else {
        await handlePlatformInNewTabOpen()
        navigate('/dashboard/traders-room/trading-accounts/demo')
      }
      return
    }

    if (isPlatformTypeMT4(tradingAccount) || isPlatformTypeMT5(tradingAccount)) {
      await handlePlatformInNewTabOpen()
      navigate('/dashboard/traders-room/trading-accounts/demo')
      return
    }

    if (isPlatformTypeTradingView(tradingAccount)) {
      window.open(tradingViewUrl, '_blank')
      navigate('/dashboard/traders-room/trading-accounts/demo')
    }
  }

  if (!location.state?.canAccessSuccessPage) {
    return <Navigate to='/dashboard/traders-room/trading-accounts/demo' replace />
  }

  return (
    <CreateTradingAccountCampaignSuccessPageTemplate
      tradingAccount={tradingAccount}
      isLoading={isLoading}
      onSubmit={handleSubmit}
    />
  )
}

const CreateTradingAccountCampaignSuccessPageTemplate: React.FC<{
  tradingAccount?: TradingAccountType
  isLoading: boolean
  onSubmit: () => Promise<void> | void
}> = (props) => {
  const { tradingAccount, isLoading, onSubmit } = props

  useScrollToTop()
  const { t } = useTranslation()

  const { account } = useAccountReadContext()
  const email = account && getClientPrimaryEmail(account)

  return (
    <Loading isLoading={isLoading}>
      <Box flex direction='column' align='center' justifySelf='center' gap='x32' maxWidth={632}>
        <ContentBlock
          variant='voucher'
          icon={tradingAccount && <PlatformIcon tradingAccount={tradingAccount} />}
          title={t('Trading Account.Trading Account is Created')}
          description={t(
            'Trading Account.Please check your email {email} for your trading account details',
            { email }
          )}
        />

        <Box
          radius='x16'
          gap='x8'
          p='x16'
          direction='column'
          fullWidth
          border={{ color: 'outline-low-em', style: 'solid' }}
        >
          {tradingAccount && <SuccessDetailsBlock tradingAccount={tradingAccount} />}
        </Box>

        <Box flex maxWidth={320} justifySelf='center' fullWidth>
          <Button fullWidth size='lg' variant='primary' type='button' onClick={onSubmit}>
            {t('Trading Account.Start trading')}
          </Button>
        </Box>
      </Box>
    </Loading>
  )
}

const SuccessDetailsBlock: React.FC<{ tradingAccount: TradingAccountType }> = ({
  tradingAccount,
}) => {
  const { t } = useTranslation()
  const platformId = tradingAccount?.platformType?.id
  const leverageName = getLeverageName(tradingAccount)

  if (isMT4PlatformType(platformId) || isMT5PlatformType(platformId)) {
    return (
      <>
        <DetailRow label={t('Trading Account.Platform')} value={tradingAccount.platformType.name} />
        <DetailRow label={t('Trading Account.Leverage')} value={leverageName} />
        <DetailRow
          label={t('Trading Account.Login')}
          value={tradingAccount.platformOverview.login}
          copyable
        />
        <DetailRow
          label={t('Trading Account.Server')}
          value={tradingAccount.server.publicName}
          copyable
        />
      </>
    )
  }

  if (isTMTPlatformType(platformId) || isTradingViewPlatformType(platformId)) {
    return (
      <>
        <DetailRow label={t('Trading Account.Platform')} value={tradingAccount.platformType.name} />
        <DetailRow label={t('Trading Account.Leverage')} value={leverageName} />
        <DetailRow
          label={t('Trading Account.User ID')}
          value={tradingAccount.externalId}
          copyable
        />
      </>
    )
  }

  return null
}

const DetailRow: React.FC<{ label: string; value?: string; copyable?: boolean }> = ({
  label,
  value,
  copyable,
}) => (
  <Box flex justify='space-between' align='center' py='x4' minHeight={48}>
    <Body2 color='text-med-em'>{label}</Body2>
    <Box flex align='center'>
      <Body2>{value}</Body2>
      {copyable && value && (
        <Box>
          <CopyLink value={value}>
            <IconButton icon={CopyIcon} variant='ghost' />
          </CopyLink>
        </Box>
      )}
    </Box>
  </Box>
)

const PlatformIcon: React.FC<{ tradingAccount: TradingAccountType }> = ({ tradingAccount }) => {
  const platformId = tradingAccount?.platformType?.id

  if (isMT4PlatformType(platformId)) {
    return <RichIcon icon={{ icon: MT4Icon, color: 'text-inverse-black-neutral' }} size='x56' />
  }
  if (isMT5PlatformType(platformId)) {
    return <RichIcon icon={{ icon: MT5Icon, color: 'text-inverse-black-neutral' }} size='x56' />
  }
  if (isTMTPlatformType(platformId)) {
    return <RichIcon icon={{ icon: TMTIcon, color: 'text-inverse-black-neutral' }} size='x56' />
  }
  if (isTradingViewPlatformType(platformId)) {
    return (
      <RichIcon icon={{ icon: TradingViewIcon, color: 'text-inverse-black-neutral' }} size='x56' />
    )
  }
  return null
}

const isEmptyAccountsError = (error: unknown) => {
  return error instanceof Error && error.message === EMPTY_ACCOUNTS_ERROR
}

const getLeverageName = (account: TradingAccountType): string | undefined => {
  if ('leverageType' in account && account.leverageType) {
    return account.leverageType.name
  }
  if ('leverage' in account && account.leverage) {
    return account.leverage.name
  }
  return undefined
}
